// src/components/FamilyDetail.js
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Spinner, Alert, Modal, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus,
    faPencilAlt,
    faTrashAlt,
    faImages,
    faArrowLeft,
    faMapMarkerAlt,
    faPhone,
    faHeart,
    faEnvelope,
    faBirthdayCake,
    faUsers,
    faUserPlus,
    faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { format, isValid } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import MemberPhotoAvatar from './MemberPhotoAvatar';
import FormattedPhoneNumber from './FormattedPhoneNumber';
import HistorySection from './HistorySection';
import { familyService, memberService } from '../services';

import './FamilyDetail.css';

const MAIN_FAMILY_PHOTO_PLACEHOLDER = '/assets/blank.png';

// Helper: Format Birthday from new string fields (without year for privacy)
const formatBirthday = (member) => {
    if (member && member.birthdayMonth && member.birthdayDay) {
        const month = parseInt(member.birthdayMonth, 10);
        const day = parseInt(member.birthdayDay, 10);
        // Use placeholder year just for date formatting
        const dateObj = new Date(2000, month - 1, day);
        if (isValid(dateObj) && dateObj.getMonth() === month - 1) {
            // Never show year for privacy
            return format(dateObj, 'MMM d');
        }
    }
    return null;
};

// Helper: Format Anniversary from V2 (string fields) data (without year for privacy)
const formatFamilyAnniversary = (family) => {
    if (family.anniversaryMonth && family.anniversaryDay) {
        const month = parseInt(family.anniversaryMonth, 10);
        const day = parseInt(family.anniversaryDay, 10);
        // Use placeholder year just for date formatting
        const dateObj = new Date(2000, month - 1, day);
        if (isValid(dateObj) && dateObj.getMonth() === month - 1) {
            // Never show year for privacy
            return format(dateObj, 'MMM d');
        }
    }
    return null;
};

// Helper: Format full address
const formatAddress = (family) => {
    const parts = [];
    if (family.street) parts.push(family.street);
    const cityStateZip = [
        family.city,
        family.state,
        family.zipcode
    ].filter(Boolean).join(family.city && family.state ? ', ' : ' ');
    if (cityStateZip) parts.push(cityStateZip);
    return parts.length > 0 ? parts : null;
};

function FamilyDetail() {
    const { familyId } = useParams();
    const navigate = useNavigate();
    const { currentUser, isAdmin, userFidClaim, loadingAuth } = useAuth();

    // State for Family Data
    const [family, setFamily] = useState(null);
    const [photoURL, setPhotoURL] = useState(MAIN_FAMILY_PHOTO_PLACEHOLDER);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // State for Member Data
    const [members, setMembers] = useState([]);
    const [loadingMembers, setLoadingMembers] = useState(true);
    const [errorMembers, setErrorMembers] = useState(null);

    // Delete loading states
    const [deletingFamily, setDeletingFamily] = useState(false);
    const [deletingMemberId, setDeletingMemberId] = useState(null);

    // Delete modal state
    const [deleteModal, setDeleteModal] = useState({ show: false, type: null, id: null, name: '' });

    // Filter deceased members - only admins can see them
    const visibleMembers = useMemo(() => {
        if (isAdmin) {
            return members; // Admins see all members
        }
        return members.filter(m => m.status !== 'deceased');
    }, [members, isAdmin]);

    // Effect 1: Fetch Family Data & Photo URL
    useEffect(() => {
        if (loadingAuth || !familyId) {
            if (!loadingAuth && !familyId) {
                setError("Family ID missing.");
                setLoading(false);
                setLoadingMembers(false);
            }
            return;
        }

        setLoading(true);
        setLoadingMembers(true);
        setError(null);
        setErrorMembers(null);
        setFamily(null);
        setMembers([]);
        setPhotoURL(MAIN_FAMILY_PHOTO_PLACEHOLDER);

        let isMounted = true;

        const fetchFamilyAndPhoto = async () => {
            try {
                const familyData = await familyService.getFamily(familyId);
                if (!isMounted) return;

                if (familyData) {
                    setFamily(familyData);
                    if (familyData.photo) {
                        if (familyData.photo.startsWith('http')) {
                            if (isMounted) setPhotoURL(familyData.photo);
                        } else {
                            try {
                                const url = await familyService.getFamilyPhotoUrl(familyData.photo, familyData.imageUpdatedAt);
                                if (isMounted && url) setPhotoURL(url);
                            } catch (photoError) {
                                console.error(`Error getting photo URL for ${familyData.photo}:`, photoError);
                            }
                        }
                    }
                } else {
                    if (isMounted) setError('Family not found.');
                    if (isMounted) setLoadingMembers(false);
                }
            } catch (err) {
                console.error("Error fetching family:", err);
                if (isMounted) setError(err.message || "An error occurred fetching family details.");
                if (isMounted) setLoadingMembers(false);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchFamilyAndPhoto();
        return () => { isMounted = false; };
    }, [familyId, loadingAuth, currentUser]);

    // Effect 2: Fetch Members
    useEffect(() => {
        if (!family || !family.id || !family.orgId) {
            setLoadingMembers(false);
            setMembers([]);
            return;
        }

        const currentFamilyId = family.id;
        let isMounted = true;

        const fetchMembersData = async () => {
            if (!isMounted) return;
            setLoadingMembers(true);
            setErrorMembers(null);

            try {
                const memberList = await memberService.getMembersByFamily(currentFamilyId);
                if (!isMounted) return;
                setMembers(memberList);
            } catch (err) {
                if (isMounted) {
                    if (err.code === 'failed-precondition' && err.message?.includes('index')) {
                        setErrorMembers("Database index missing. Check console.");
                    } else {
                        setErrorMembers(err.message || "Failed to load family members.");
                    }
                }
            } finally {
                if (isMounted) setLoadingMembers(false);
            }
        };

        fetchMembersData();
        return () => { isMounted = false; };
    }, [family]);

    // Handler: Open delete confirmation modal
    const openDeleteModal = (type, id, name) => {
        setDeleteModal({ show: true, type, id, name });
    };

    const closeDeleteModal = () => {
        if (!deletingFamily && !deletingMemberId) {
            setDeleteModal({ show: false, type: null, id: null, name: '' });
        }
    };

    // Handler: Confirm delete from modal
    const handleConfirmDelete = async () => {
        const { type, id, name } = deleteModal;
        setError(null);
        setErrorMembers(null);

        if (type === 'member') {
            setDeletingMemberId(id);
            try {
                const result = await memberService.deleteMember(id);
                if (result.success) {
                    setMembers(prev => prev.filter(m => m.id !== id));
                    setDeleteModal({ show: false, type: null, id: null, name: '' });
                } else {
                    setErrorMembers(result.error || "Server denied the member deletion request.");
                }
            } catch (err) {
                setError(`Failed to delete member: ${err.message || "Network/Function error"}`);
            } finally {
                setDeletingMemberId(null);
            }
        } else if (type === 'family') {
            setDeletingFamily(true);
            try {
                const result = await familyService.deleteFamily(id);
                if (result.success) {
                    navigate('/families', { replace: true, state: { message: `${name || 'Family'} deleted successfully.` } });
                } else {
                    setError(result.error || "Server denied the family deletion request.");
                }
            } catch (err) {
                setError(`Failed to delete family: ${err.message || "Network/Function error"}`);
            } finally {
                setDeletingFamily(false);
            }
        }
    };

    // Loading state - skeleton
    if (loadingAuth || loading) {
        return (
            <div className="family-detail-page">
                <div className="family-detail-loading">
                    <div className="skeleton-header"></div>
                    <div className="skeleton-hero"></div>
                    <div className="skeleton-card"></div>
                </div>
            </div>
        );
    }

    // Not logged in
    if (!currentUser) {
        return (
            <div className="family-detail-page">
                <div className="family-detail-header">
                    <div className="container">
                        <div className="family-detail-header-content">
                            <div className="family-detail-header-left">
                                <h1 className="family-detail-title">Family Details</h1>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="container">
                    <Alert variant="warning">
                        Please <Alert.Link as={Link} to="/login">log in</Alert.Link> to view family details.
                    </Alert>
                </div>
            </div>
        );
    }

    // Error or no family
    if (error || !family) {
        return (
            <div className="family-detail-page">
                <div className="family-detail-header">
                    <div className="container">
                        <div className="family-detail-header-content">
                            <div className="family-detail-header-left">
                                <Link to="/families" className="family-back-btn">
                                    <FontAwesomeIcon icon={faArrowLeft} />
                                </Link>
                                <h1 className="family-detail-title">Family Details</h1>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="container">
                    <div className="family-detail-content">
                        <Alert variant="danger">
                            {error || "Family data could not be loaded."}
                        </Alert>
                    </div>
                </div>
            </div>
        );
    }

    const canModifyFamilyData = isAdmin || (userFidClaim && family && userFidClaim === family.id);
    const addressParts = family ? formatAddress(family) : null;
    const anniversary = family ? formatFamilyAnniversary(family) : null;

    return (
        <div className="family-detail-page">
            {/* Page Header */}
            <div className="family-detail-header">
                <div className="container">
                    <div className="family-detail-header-content">
                        <div className="family-detail-header-left">
                            <Link to="/families" className="family-back-btn" title="Back to Families">
                                <FontAwesomeIcon icon={faArrowLeft} />
                            </Link>
                            <div>
                                <h1 className="family-detail-title">{family.surname || 'Family'} Family</h1>
                                <p className="family-detail-subtitle">{visibleMembers.length} member{visibleMembers.length !== 1 ? 's' : ''}</p>
                            </div>
                        </div>
                        {canModifyFamilyData && (
                            <div className="family-header-actions">
                                <button
                                    className="family-action-btn family-action-btn-danger"
                                    title="Delete Family"
                                    onClick={() => openDeleteModal('family', family.id, family.surname)}
                                    disabled={deletingFamily}
                                >
                                    <FontAwesomeIcon icon={deletingFamily ? faSpinner : faTrashAlt} spin={deletingFamily} />
                                    <span>{deletingFamily ? 'Deleting...' : 'Delete'}</span>
                                </button>
                                <Link
                                    to={`/families/edit/${family.id}`}
                                    className="family-action-btn family-action-btn-primary"
                                    title="Edit Family"
                                >
                                    <FontAwesomeIcon icon={faPencilAlt} />
                                    <span>Edit</span>
                                </Link>
                                <Link
                                    to={`/families/${family.id}/assign-photos/all`}
                                    className="family-action-btn family-action-btn-primary"
                                    title="Assign Photos"
                                >
                                    <FontAwesomeIcon icon={faImages} />
                                    <span>Photos</span>
                                </Link>
                                <Link
                                    to="/members/add"
                                    state={{ preselectedFamilyId: family.id, preselectedFamilyName: family.surname }}
                                    className="family-action-btn family-action-btn-add"
                                    title="Add Member"
                                >
                                    <FontAwesomeIcon icon={faUserPlus} />
                                    <span>Add Member</span>
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="container">
                <div className="family-detail-content">
                    {/* Error Alert */}
                    {error && (
                        <Alert variant="danger" className="mb-3" onClose={() => setError(null)} dismissible>
                            {error}
                        </Alert>
                    )}

                    {/* Hero Section - Photo + Quick Info */}
                    <div className="family-hero" style={{ position: 'relative' }}>
                        {deletingFamily && (
                            <div className="deleting-overlay">
                                <FontAwesomeIcon icon={faSpinner} spin className="deleting-overlay-spinner" />
                                <span className="deleting-overlay-text">Deleting family...</span>
                            </div>
                        )}
                        <div className="family-photo-container">
                            <img
                                src={photoURL}
                                alt={`${family.surname || 'Family'}`}
                                className="family-photo"
                                onError={(e) => {
                                    if (e.target.src !== MAIN_FAMILY_PHOTO_PLACEHOLDER) {
                                        e.target.onerror = null;
                                        e.target.src = MAIN_FAMILY_PHOTO_PLACEHOLDER;
                                    }
                                }}
                            />
                            {canModifyFamilyData && (
                                <Link
                                    to={`/families/edit/${family.id}`}
                                    className="family-photo-edit"
                                    title="Edit Photo"
                                >
                                    <FontAwesomeIcon icon={faPencilAlt} />
                                    Edit
                                </Link>
                            )}
                        </div>
                        <div className="family-quick-info">
                            <div className="family-contact-grid">
                                {/* Address */}
                                {addressParts && (
                                    <div className="family-contact-item">
                                        <div className="family-contact-icon family-contact-icon-address">
                                            <FontAwesomeIcon icon={faMapMarkerAlt} />
                                        </div>
                                        <div className="family-contact-content">
                                            <div className="family-contact-label">Address</div>
                                            <div className="family-contact-value">
                                                {addressParts.map((part, i) => (
                                                    <span key={i}>{part}{i < addressParts.length - 1 && <br />}</span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Phone */}
                                {family.phone && (
                                    <div className="family-contact-item">
                                        <div className="family-contact-icon family-contact-icon-phone">
                                            <FontAwesomeIcon icon={faPhone} />
                                        </div>
                                        <div className="family-contact-content">
                                            <div className="family-contact-label">Phone</div>
                                            <div className="family-contact-value">
                                                <a href={`tel:${family.phone}`}>
                                                    <FormattedPhoneNumber phoneNumber={family.phone} />
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Anniversary */}
                                {anniversary && (
                                    <div className="family-contact-item">
                                        <div className="family-contact-icon family-contact-icon-anniversary">
                                            <FontAwesomeIcon icon={faHeart} />
                                        </div>
                                        <div className="family-contact-content">
                                            <div className="family-contact-label">Anniversary</div>
                                            <div className="family-contact-value">{anniversary}</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Members Section */}
                    <div className="family-members-section">
                        <div className="family-members-header">
                            <h3 className="family-members-title">
                                <FontAwesomeIcon icon={faUsers} />
                                Family Members
                                <span className="family-members-count">{visibleMembers.length}</span>
                            </h3>
                            {canModifyFamilyData && (
                                <Link
                                    to="/members/add"
                                    state={{ preselectedFamilyId: family.id, preselectedFamilyName: family.surname }}
                                    className="family-members-add-btn"
                                >
                                    <FontAwesomeIcon icon={faPlus} />
                                    Add Member
                                </Link>
                            )}
                        </div>
                        <div className="family-members-body">
                            {loadingMembers ? (
                                <div className="family-members-loading">
                                    <Spinner animation="border" size="sm" />
                                    <span>Loading members...</span>
                                </div>
                            ) : errorMembers ? (
                                <Alert variant="warning">{errorMembers}</Alert>
                            ) : visibleMembers.length === 0 ? (
                                <div className="family-members-empty">
                                    <div className="family-members-empty-icon">
                                        <FontAwesomeIcon icon={faUsers} />
                                    </div>
                                    <h4>No Members Yet</h4>
                                    <p>
                                        {canModifyFamilyData
                                            ? "Add family members to get started."
                                            : "No members have been added to this family yet."
                                        }
                                    </p>
                                    {canModifyFamilyData && (
                                        <Link
                                            to="/members/add"
                                            state={{ preselectedFamilyId: family.id, preselectedFamilyName: family.surname }}
                                            className="family-members-add-btn"
                                            style={{ marginTop: '1rem' }}
                                        >
                                            <FontAwesomeIcon icon={faPlus} />
                                            Add First Member
                                        </Link>
                                    )}
                                </div>
                            ) : (
                                <div className="member-cards-grid">
                                    {visibleMembers.map(member => {
                                        if (!member || !member.id) return null;

                                        const photoCacheKey = member.imageUpdatedAt?.seconds
                                            ? (member.imageUpdatedAt.seconds * 1000).toString()
                                            : (member.imageUpdatedAt || member.id);

                                        const birthday = formatBirthday(member);
                                        const isDeceased = member.status === 'deceased';

                                        return (
                                            <div key={member.id} className={`member-card ${isDeceased ? 'member-card-deceased' : ''}`} style={{ position: 'relative' }}>
                                                {deletingMemberId === member.id && (
                                                    <div className="deleting-overlay">
                                                        <FontAwesomeIcon icon={faSpinner} spin className="deleting-overlay-spinner" />
                                                        <span className="deleting-overlay-text">Deleting...</span>
                                                    </div>
                                                )}
                                                <div className="member-card-photo">
                                                    <Link to={`/families/${family.id}/member/${member.id}`}>
                                                        <MemberPhotoAvatar
                                                            src={member.photo}
                                                            alt={member.name || 'Member'}
                                                            size={64}
                                                            cacheKey={photoCacheKey}
                                                        />
                                                    </Link>
                                                </div>
                                                <div className="member-card-info">
                                                    <h4 className="member-card-name">
                                                        <Link to={`/families/${family.id}/member/${member.id}`}>
                                                            {member.name || 'No Name'}
                                                            {isDeceased && <span className="member-deceased-badge">Deceased</span>}
                                                        </Link>
                                                    </h4>
                                                    <div className="member-card-details">
                                                        {birthday && (
                                                            <div className="member-card-detail">
                                                                <FontAwesomeIcon icon={faBirthdayCake} className="member-card-detail-icon" />
                                                                {birthday}
                                                            </div>
                                                        )}
                                                        {member.cellphone && (
                                                            <div className="member-card-detail">
                                                                <FontAwesomeIcon icon={faPhone} className="member-card-detail-icon" />
                                                                <a href={`tel:${member.cellphone}`}>
                                                                    <FormattedPhoneNumber phoneNumber={member.cellphone} />
                                                                </a>
                                                            </div>
                                                        )}
                                                        {member.email && (
                                                            <div className="member-card-detail">
                                                                <FontAwesomeIcon icon={faEnvelope} className="member-card-detail-icon" />
                                                                <a href={`mailto:${member.email}`}>
                                                                    {member.email}
                                                                </a>
                                                            </div>
                                                        )}
                                                    </div>
                                                    {canModifyFamilyData && (
                                                        <div className="member-card-actions">
                                                            <Link
                                                                to={`/members/edit/${member.id}`}
                                                                className="member-action-btn member-action-btn-edit"
                                                                title={`Edit ${member.name || 'Member'}`}
                                                            >
                                                                <FontAwesomeIcon icon={faPencilAlt} />
                                                            </Link>
                                                            <button
                                                                className="member-action-btn member-action-btn-delete"
                                                                title={`Delete ${member.name || 'Member'}`}
                                                                onClick={() => openDeleteModal('member', member.id, member.name)}
                                                            >
                                                                <FontAwesomeIcon icon={faTrashAlt} />
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* History Section - includes family and member changes */}
                    {canModifyFamilyData && (
                        <HistorySection entityType="FAMILY" entityId={family.id} includeFamilyMembers={true} />
                    )}

                </div>
            </div>

            {/* Delete Confirmation Modal */}
            <Modal show={deleteModal.show} onHide={closeDeleteModal} centered className="delete-confirm-modal">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FontAwesomeIcon icon={faTrashAlt} />
                        {deleteModal.type === 'family' ? 'Delete Family' : 'Delete Member'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="delete-modal-message">
                        {deleteModal.type === 'family'
                            ? `Are you sure you want to delete the ${deleteModal.name || ''} family?`
                            : `Are you sure you want to permanently delete ${deleteModal.name || 'this member'}?`
                        }
                    </p>
                    {deleteModal.type === 'family' && (
                        <div className="delete-modal-warning">
                            This will also delete <strong>all family members</strong>. This action cannot be undone.
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-secondary"
                        onClick={closeDeleteModal}
                        disabled={deletingFamily || !!deletingMemberId}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleConfirmDelete}
                        disabled={deletingFamily || !!deletingMemberId}
                    >
                        {(deletingFamily || deletingMemberId) ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                Deleting...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faTrashAlt} className="me-2" />
                                Delete
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default FamilyDetail;
